import { useState, useEffect, useRef } from 'react';
import { useSwipeable } from 'react-swipeable';
import * as pdfjsLib from 'pdfjs-dist';
import { Book } from '../../types';
import { useStore, TocItem } from '../../store/useStore';
import TextSelectionMenu from './TextSelectionMenu';
import NoteModal from './NoteModal';
import type { HighlightColor, Highlight, Note } from '../../types';
import { getPDFFile } from '../../utils/fileStorage';
import { FiChevronLeft, FiChevronRight, FiZoomIn, FiZoomOut, FiMaximize2 } from 'react-icons/fi';

pdfjsLib.GlobalWorkerOptions.workerSrc = `${import.meta.env.BASE_URL}pdf.worker.min.mjs`;

// 전역 타입 선언
interface PDFSearchMatch {
  pageNumber: number;
  text: string;
  excerpt: string;
}

declare global {
  interface Window {
    pdfGoToPage?: (page: number) => void;
    pdfGetCurrentText?: () => Promise<string>;
    // S2-2: 전체 문서 검색 — 기존 pdfGetCurrentText는 현재 페이지 하나만 훑어
    // 다른 쪽으로 이동할 결과 자체가 안 나왔다(SearchPanel.tsx 참고). getTextContent를
    // 페이지마다 다시 부르는 대신, 로드 시점에 한 번 훑어 만든 표(pdfPageTextCacheRef)를
    // 그대로 검색한다 — 매 검색마다 N페이지를 다시 추출하면 느리다.
    pdfSearchAllPages?: (query: string) => Promise<PDFSearchMatch[]>;
    pdfCurrentPage?: number;
    pdfTotalPages?: number;
  }
}

interface PDFViewerProps {
  book: Book;
}

const PDFViewer = ({ book }: PDFViewerProps) => {
  const { updateBook, setTocItems, clearToc, highlights, addHighlight, notes, addNote } = useStore();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  // PDF 하이라이트(2026-09-14): 캔버스 위에 pdf.js TextLayer를 겹쳐 텍스트를 선택
  // 가능하게 만든다. 종전엔 PDF가 캔버스뿐이라 드래그 자체가 안 됐고, 그래서
  // 하이라이트 패널은 있는데 PDF에서는 만들 방법이 아예 없었다.
  const textLayerRef = useRef<HTMLDivElement>(null);
  // 딥QA 재점검(2026-09-14): 렌더는 비동기라 페이지를 빠르게 넘기면 이전 렌더가
  // 아직 끝나지 않은 채 다음 렌더가 시작될 수 있다. 그러면 두 실행이 같은 텍스트
  // 레이어에 각자 span을 덧붙여 글자가 겹치고, 그 상태에서 만든 하이라이트는
  // 엉뚱한 좌표로 저장된다. 세대 번호로 "가장 마지막 렌더만 반영"하게 만든다.
  const renderGenerationRef = useRef(0);
  // 진행 중인 pdf.js RenderTask. 새 렌더를 시작하기 전에 이걸 취소해야 한다 —
  // 같은 캔버스에 두 렌더를 동시에 걸면 pdf.js가 예외를 던진다.
  const renderTaskRef = useRef<any>(null);
  const pageWrapRef = useRef<HTMLDivElement>(null);
  const [selectedText, setSelectedText] = useState('');
  const [selectionRects, setSelectionRects] = useState<{ x: number; y: number; w: number; h: number }[]>([]);
  const [showSelectionMenu, setShowSelectionMenu] = useState(false);
  const [selectionMenuPos, setSelectionMenuPos] = useState({ x: 0, y: 0 });
  const [showNoteModal, setShowNoteModal] = useState(false);
  const pdfDocRef = useRef<pdfjsLib.PDFDocumentProxy | null>(null);
  // S2-2: 페이지별 추출 텍스트 캐시 — pdfSearchAllPages가 검색마다 매번
  // getTextContent를 다시 부르지 않도록 첫 조회 결과를 페이지 번호로 쌓아 둔다.
  // 책이 바뀌면(loadPDF) 비워야 이전 책 텍스트가 섞이지 않는다.
  const pdfPageTextCacheRef = useRef<Map<number, string>>(new Map());
  const [pdfDoc, setPdfDoc] = useState<pdfjsLib.PDFDocumentProxy | null>(null);
  const [currentPage, setCurrentPage] = useState(book.currentPage || 1);
  const [totalPages, setTotalPages] = useState(book.totalPages || 0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [scale, setScale] = useState(1.5);

  // 스와이프 제스처
  const swipeHandlers = useSwipeable({
    onSwipedLeft: () => goToNextPage(),
    onSwipedRight: () => goToPrevPage(),
    preventScrollOnSwipe: true,
    trackMouse: false,
  });

  // ✅ 페이지 이동 함수 (useCallback으로 안정화)

  // Load PDF from IndexedDB
  useEffect(() => {
    const loadPDF = async () => {
      try {
        setLoading(true);
        setError(null);

        console.log(`📖 PDF 초기화 시작: ${book.title}`);

        const arrayBuffer = await getPDFFile(book.filePath);

        if (!arrayBuffer) {
          throw new Error('PDF_NOT_FOUND');
        }

        const loadingTask = pdfjsLib.getDocument({ data: arrayBuffer });
        const pdf = await loadingTask.promise;

        setPdfDoc(pdf);
        pdfDocRef.current = pdf;
        pdfPageTextCacheRef.current = new Map(); // S2-2: 새 책이면 이전 책 텍스트 캐시 폐기
        setTotalPages(pdf.numPages);
        
        // 전역 변수 설정
        window.pdfTotalPages = pdf.numPages;

        updateBook(book.id, { totalPages: pdf.numPages });

        console.log(`✅ PDF 로드 완료: ${pdf.numPages} 페이지`);

        // ✅ PDF 목차(아웃라인) 로드
        try {
          const outline = await pdf.getOutline();
          console.log('📚 PDF 아웃라인:', outline);
          
          // 아웃라인이 있고 5개 이상일 때만 사용
          if (outline && outline.length >= 5) {
            const convertOutline = async (items: any[], parentIndex: string = ''): Promise<TocItem[]> => {
              const result: TocItem[] = [];
              
              for (let i = 0; i < items.length; i++) {
                const item = items[i];
                let pageNumber = 1;
                
                if (item.dest) {
                  try {
                    let dest = item.dest;
                    if (typeof dest === 'string') {
                      dest = await pdf.getDestination(dest);
                    }
                    if (dest && dest[0]) {
                      const pageRef = dest[0];
                      const pageIndex = await pdf.getPageIndex(pageRef);
                      pageNumber = pageIndex + 1;
                    }
                  } catch (e) {
                    console.warn('페이지 번호 추출 실패:', e);
                  }
                }
                
                const tocItem: TocItem = {
                  id: `pdf-toc-${parentIndex}${i}`,
                  label: item.title || `항목 ${i + 1}`,
                  href: '',
                  pageNumber: pageNumber,
                  subitems: item.items?.length ? await convertOutline(item.items, `${parentIndex}${i}-`) : undefined,
                };
                result.push(tocItem);
              }
              
              return result;
            };
            
            const tocItems = await convertOutline(outline);
            setTocItems(tocItems, 'pdf');
            console.log('   ✅ PDF 목차 저장 완료:', tocItems.length, '개 항목');
          } else {
            // ✅ 아웃라인이 없거나 적으면 페이지 기반 목차 생성
            console.log('   📄 아웃라인 부족, 페이지 기반 목차 생성');
            const pageToc: TocItem[] = [];
            const step = pdf.numPages > 100 ? 10 : (pdf.numPages > 50 ? 5 : 1);
            
            for (let i = 1; i <= pdf.numPages; i += step) {
              pageToc.push({
                id: `pdf-page-${i}`,
                label: `${i}페이지`,
                href: '',
                pageNumber: i,
              });
            }
            // 마지막 페이지 추가
            if (pageToc.length > 0 && pageToc[pageToc.length - 1].pageNumber !== pdf.numPages) {
              pageToc.push({
                id: `pdf-page-${pdf.numPages}`,
                label: `${pdf.numPages}페이지 (마지막)`,
                href: '',
                pageNumber: pdf.numPages,
              });
            }
            setTocItems(pageToc, 'pdf');
            console.log('   📄 페이지 기반 목차 생성:', pageToc.length, '개');
          }
        } catch (outlineErr) {
          console.warn('PDF 아웃라인 로드 실패:', outlineErr);
        }

        setLoading(false);
      } catch (err: any) {
        console.error('❌ PDF 로드 실패:', err);

        if (err.message === 'PDF_NOT_FOUND') {
          setError('PDF 파일을 찾을 수 없습니다. 다시 업로드해주세요.');
        } else {
          setError('PDF를 불러오는 중 오류가 발생했습니다.');
        }

        setLoading(false);
      }
    };

    loadPDF();

    return () => {
      window.pdfGoToPage = undefined;
      window.pdfGetCurrentText = undefined;
      window.pdfSearchAllPages = undefined;
      window.pdfCurrentPage = undefined;
      window.pdfTotalPages = undefined;
      clearToc();
    };
  }, [book.filePath]);

  // ✅ 전역 함수 등록 (currentPage, totalPages 변경 시 업데이트)
  useEffect(() => {
    // 페이지 이동 함수
    window.pdfGoToPage = (page: number) => {
      console.log('🔗 pdfGoToPage 호출:', page, '/ 총', totalPages);
      if (page >= 1 && page <= totalPages) {
        setCurrentPage(page);
      }
    };
    
    // 현재 페이지 번호
    window.pdfCurrentPage = currentPage;
    
    // TTS용 현재 페이지 텍스트 추출
    if (pdfDocRef.current) {
      const pdf = pdfDocRef.current;
      window.pdfGetCurrentText = async () => {
        try {
          console.log('🔊 PDF 텍스트 추출 시작 - 페이지:', currentPage);
          const page = await pdf.getPage(currentPage);
          const textContent = await page.getTextContent();
          const text = textContent.items
            .map((item: any) => item.str)
            .join(' ')
            .replace(/\s+/g, ' ')
            .trim();
          console.log('🔊 PDF 텍스트 추출 완료:', text.slice(0, 100) + '...');
          return text;
        } catch (e) {
          console.error('PDF 텍스트 추출 실패:', e);
          return '';
        }
      };

      // S2-2: 전체 문서 검색. currentPage 하나만 보던 SearchPanel의 옛 방식은
      // 지금 보고 있는 페이지 말고는 결과가 안 나와 "이동"이 사실상 없는 것과 같았다.
      // pdfPageTextCacheRef에 페이지별로 쌓아 두고, 이미 뽑은 페이지는 재사용한다.
      window.pdfSearchAllPages = async (query: string) => {
        const results: PDFSearchMatch[] = [];
        if (!query.trim()) return results;

        const MAX_RESULTS = 50;
        const cache = pdfPageTextCacheRef.current;
        const total = pdf.numPages;
        // S2-2 딥QA 수정(2026-09-14): query를 그대로 RegExp에 넣으면 정규식 특수문자를
        // 검색어로 입력한 사용자가 캐타스트로픽 백트래킹으로 자기 탭을 스스로 멈추게
        // 할 수 있었다 — 검색어는 항상 리터럴 문자열로 취급한다. 이스케이프하면 무효
        // 정규식이 나올 수 없지만 try/catch는 방어적으로 남겨둔다.
        const escapeRegExp = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        let regex: RegExp;
        try {
          regex = new RegExp(escapeRegExp(query), 'gi');
        } catch {
          return results;
        }

        for (let p = 1; p <= total && results.length < MAX_RESULTS; p++) {
          let text = cache.get(p);
          if (text === undefined) {
            try {
              const pg = await pdf.getPage(p);
              const textContent = await pg.getTextContent();
              text = textContent.items
                .map((item: any) => item.str)
                .join(' ')
                .replace(/\s+/g, ' ')
                .trim();
              cache.set(p, text); // 성공했을 때만 캐시 — 실패는 다음 검색에서 재시도
            } catch (e) {
              console.error(`PDF 검색: ${p}페이지 텍스트 추출 실패:`, e);
              // S2-2 딥QA 수정: 실패를 캐시하지 않는다 — 일시적 오류(워커 글리치 등)면
              // 다음 검색에서 재시도돼야 하는데, 예전엔 빈 문자열이 영구 캐시돼 그
              // 페이지는 책이 열려 있는 동안 다시는 검색되지 않았다.
              text = '';
            }
          }

          if (!text) continue;
          regex.lastIndex = 0;
          let match: RegExpExecArray | null;
          while ((match = regex.exec(text)) !== null && results.length < MAX_RESULTS) {
            const start = Math.max(0, match.index - 30);
            const end = Math.min(text.length, match.index + query.length + 30);
            results.push({
              pageNumber: p,
              text: match[0],
              excerpt: `...${text.substring(start, end)}...`,
            });
            if (match.index === regex.lastIndex) regex.lastIndex++; // 빈 매치 무한루프 방지
          }
        }

        return results;
      };
    }
  }, [currentPage, totalPages, pdfDoc]); // S2-2 딥QA 수정(2026-09-14): totalPages가 book.totalPages와
  // 우연히 같은 값으로 세팅되면(이미 한 번 읽었던 책) setTotalPages가 리렌더를 안 일으켜서 이 effect가
  // 로드 완료 후에도 재실행되지 않았다 — 첫 페이지 이동 전까지 pdfSearchAllPages가 등록 안 됨(실측).
  // pdfDoc은 null → 실제 문서로 로드당 정확히 한 번만 바뀌므로 이걸 의존성에 넣어 로드 완료 시점에
  // 확실히 재실행시킨다.


  // 드래그가 끝나면 선택 영역을 읽어 메뉴를 띄운다. 좌표는 캔버스 기준 비율로
  // 바꿔 저장한다 — 확대/축소를 바꿔도 같은 자리에 다시 그릴 수 있어야 한다.
  const handleTextSelection = () => {
    const selection = window.getSelection();
    const text = selection?.toString().trim() || '';
    if (!selection || selection.rangeCount === 0 || !text) {
      setShowSelectionMenu(false);
      return;
    }
    const layer = textLayerRef.current;
    if (!layer || !layer.contains(selection.anchorNode)) {
      setShowSelectionMenu(false);
      return;
    }

    const layerBox = layer.getBoundingClientRect();
    const rects = Array.from(selection.getRangeAt(0).getClientRects())
      .filter((r) => r.width > 0 && r.height > 0)
      .map((r) => ({
        x: (r.left - layerBox.left) / layerBox.width,
        y: (r.top - layerBox.top) / layerBox.height,
        w: r.width / layerBox.width,
        h: r.height / layerBox.height,
      }));
    if (!rects.length) {
      setShowSelectionMenu(false);
      return;
    }

    const last = selection.getRangeAt(0).getClientRects()[0];
    setSelectedText(text);
    setSelectionRects(rects);
    setSelectionMenuPos({ x: last.left + last.width / 2, y: last.top });
    setShowSelectionMenu(true);
  };

  const handleHighlight = (color: HighlightColor) => {
    if (!selectedText || !selectionRects.length) return;
    addHighlight({
      id: crypto.randomUUID(),
      bookId: book.id,
      text: selectedText,
      color,
      cfiRange: `pdf-page-${currentPage}`, // EPUB의 CFI 자리 — PDF는 쪽 번호로 식별한다
      pageNumber: currentPage,
      rects: selectionRects,
      createdAt: new Date(),
    });
    setShowSelectionMenu(false);
    window.getSelection()?.removeAllRanges();
  };

  // 메모: 선택은 하이라이트와 똑같이 잡아 두고(위 handleTextSelection), 모달에서
  // 내용을 받아 저장한다. 저장 형식도 하이라이트와 같아 패널·이동 경로를 공유한다.
  const handleNote = () => {
    setShowSelectionMenu(false);
    setShowNoteModal(true);
  };

  const handleSaveNote = (noteContent: string) => {
    if (!selectedText || !selectionRects.length) return;
    addNote({
      id: crypto.randomUUID(),
      bookId: book.id,
      text: selectedText,
      content: noteContent,
      cfiRange: `pdf-page-${currentPage}`,
      pageNumber: currentPage,
      rects: selectionRects,
      createdAt: new Date(),
    });
    setShowNoteModal(false);
    window.getSelection()?.removeAllRanges();
  };

  const pageHighlights: Highlight[] = highlights.filter(
    (h) => h.bookId === book.id && h.pageNumber === currentPage && h.rects?.length
  );

  const pageNotes: Note[] = notes.filter(
    (n) => n.bookId === book.id && n.pageNumber === currentPage && n.rects?.length
  );

  const highlightColorMap: Record<string, string> = {
    yellow: 'rgba(255, 245, 157, 0.45)',
    green: 'rgba(165, 214, 167, 0.45)',
    blue: 'rgba(144, 202, 249, 0.45)',
    pink: 'rgba(244, 143, 177, 0.45)',
    purple: 'rgba(206, 147, 216, 0.45)',
  };

  // Render current page
  useEffect(() => {
    const renderPage = async () => {
      // 딥QA 재점검(2026-09-14): 예전엔 rendering이 true면 그냥 빠져나갔는데, 그렇게
      // 건너뛴 요청을 나중에 다시 시도하는 장치가 없어서 페이지를 빠르게 넘기면
      // 화면이 마지막 요청보다 뒤처진 쪽에 멈춰 있었다(실측: 표시는 6쪽인데 캔버스와
      // 텍스트 레이어는 2쪽). 건너뛰는 대신, 진행 중이던 렌더를 취소하고 새 요청을
      // 진행한다 — pdf.js가 권장하는 방식이다.
      if (!pdfDoc || !canvasRef.current) return;

      const generation = ++renderGenerationRef.current;

      try {

        const page = await pdfDoc.getPage(currentPage);
        const canvas = canvasRef.current;
        const context = canvas.getContext('2d');

        if (!context) return;

        const viewport = page.getViewport({ scale });

        canvas.height = viewport.height;
        canvas.width = viewport.width;

        try {
          renderTaskRef.current?.cancel();
        } catch { /* 이미 끝난 태스크 */ }

        const renderTask = page.render({
          canvasContext: context,
          viewport: viewport,
        } as any);
        renderTaskRef.current = renderTask;

        try {
          await renderTask.promise;
        } catch (e: any) {
          // 취소는 정상 흐름이다(더 최신 페이지 요청이 들어왔다는 뜻).
          if (e?.name === 'RenderingCancelledException') return;
          throw e;
        }
        if (generation !== renderGenerationRef.current) return;

        // 텍스트 레이어(선택 가능한 투명 span들)를 캔버스와 같은 크기로 다시 그린다.
        const textLayerDiv = textLayerRef.current;
        if (textLayerDiv && generation === renderGenerationRef.current) {
          textLayerDiv.innerHTML = '';
          textLayerDiv.style.width = `${viewport.width}px`;
          textLayerDiv.style.height = `${viewport.height}px`;
          try {
            const textContent = await page.getTextContent();
            if (generation !== renderGenerationRef.current) return; // 더 최신 렌더가 시작됨
            const textLayer = new (pdfjsLib as any).TextLayer({
              textContentSource: textContent,
              container: textLayerDiv,
              viewport,
            });
            await textLayer.render();
          } catch (e) {
            console.warn('텍스트 레이어 생성 실패(선택/하이라이트 불가):', e);
          }
        }

        const progress = Math.round((currentPage / totalPages) * 100);
        updateBook(book.id, {
          currentPage,
          progress,
          lastRead: new Date(),
          status: currentPage === totalPages ? 'completed' : 'reading',
        });

      } catch (err) {
        console.error('❌ 페이지 렌더링 실패:', err);
      }
    };

    renderPage();
    // 딥QA 수정(2026-09-14): loading을 의존성에 넣는다. 로딩 중에는 컴포넌트가 스피너만
    // 렌더하므로 canvasRef.current가 null이고, 그 시점에 이 effect가 돌면 위 가드에서
    // 그냥 빠져나간다. setLoading(false)로 캔버스가 붙어도 의존성이 안 바뀌면 이 effect가
    // 다시 안 돌아 페이지가 영영 안 그려진다(캔버스가 기본 300px로 남음 — 실측).
  }, [pdfDoc, currentPage, scale, loading]);

  const goToPrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const goToNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const zoomIn = () => setScale(Math.min(scale + 0.2, 3.0));
  const zoomOut = () => setScale(Math.max(scale - 0.2, 0.5));
  const fitToWidth = () => setScale(1.5);

  // 키보드 네비게이션
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight' || e.key === 'PageDown') {
        goToNextPage();
      } else if (e.key === 'ArrowLeft' || e.key === 'PageUp') {
        goToPrevPage();
      }
    };
    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [currentPage, totalPages]);

  // 👆 스와이프 제스처 (모바일 페이지 넘김)
  useEffect(() => {
    let touchStartX = 0;
    let touchStartY = 0;

    const handleTouchStart = (e: TouchEvent) => {
      touchStartX = e.touches[0].clientX;
      touchStartY = e.touches[0].clientY;
    };

    const handleTouchEnd = (e: TouchEvent) => {
      const touchEndX = e.changedTouches[0].clientX;
      const touchEndY = e.changedTouches[0].clientY;
      
      const diffX = touchStartX - touchEndX;
      const diffY = touchStartY - touchEndY;
      const minSwipeDistance = 50;

      // 수평 스와이프가 수직보다 클 때만 페이지 넘김
      if (Math.abs(diffX) > Math.abs(diffY) && Math.abs(diffX) > minSwipeDistance) {
        if (diffX > 0 && currentPage < totalPages) {
          // 왼쪽으로 스와이프 → 다음 페이지
          setCurrentPage(currentPage + 1);
        } else if (diffX < 0 && currentPage > 1) {
          // 오른쪽으로 스와이프 → 이전 페이지
          setCurrentPage(currentPage - 1);
        }
      }
    };

    const container = canvasRef.current?.parentElement;
    if (container) {
      container.addEventListener('touchstart', handleTouchStart, { passive: true });
      container.addEventListener('touchend', handleTouchEnd, { passive: true });
    }

    return () => {
      if (container) {
        container.removeEventListener('touchstart', handleTouchStart);
        container.removeEventListener('touchend', handleTouchEnd);
      }
    };
  }, [currentPage, totalPages]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">PDF를 불러오는 중...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <p className="text-red-600 dark:text-red-400 mb-4">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-primary text-white rounded-lg hover:shadow-lg transition-all"
          >
            다시 시도
          </button>
        </div>
      </div>
    );
  }

  const progress = totalPages > 0 ? Math.round((currentPage / totalPages) * 100) : 0;

  return (
    <div className="flex flex-col h-full bg-gray-100 dark:bg-gray-900">
      {/* PDF Canvas */}
      <div {...swipeHandlers} className="flex-1 overflow-auto p-4">
        <div className="flex justify-center">
          <div ref={pageWrapRef} className="relative" onMouseUp={handleTextSelection} onTouchEnd={handleTextSelection}>
            <canvas
              ref={canvasRef}
              className="shadow-2xl border border-gray-300 dark:border-gray-700"
            />

            {/* 저장된 하이라이트 — 비율 좌표를 현재 캔버스 크기에 맞춰 되살린다 */}
            <div className="absolute inset-0 pointer-events-none">
              {pageHighlights.map((h) =>
                h.rects!.map((r, i) => (
                  <div
                    key={`${h.id}-${i}`}
                    className="absolute rounded-sm"
                    style={{
                      left: `${r.x * 100}%`,
                      top: `${r.y * 100}%`,
                      width: `${r.w * 100}%`,
                      height: `${r.h * 100}%`,
                      backgroundColor: highlightColorMap[h.color] || highlightColorMap.yellow,
                    }}
                  />
                ))
              )}

              {/* 메모는 하이라이트와 구분되게 밑줄로 표시한다(색칠하면 둘을 구별할 수 없다) */}
              {pageNotes.map((n) =>
                n.rects!.map((r, i) => (
                  <div
                    key={`note-${n.id}-${i}`}
                    title={n.content}
                    className="absolute"
                    style={{
                      left: `${r.x * 100}%`,
                      top: `${r.y * 100}%`,
                      width: `${r.w * 100}%`,
                      height: `${r.h * 100}%`,
                      borderBottom: '2px dashed rgba(99, 102, 241, 0.85)',
                    }}
                  />
                ))
              )}
            </div>

            {/* 선택용 텍스트 레이어(투명) */}
            <div ref={textLayerRef} className="pdf-text-layer absolute inset-0" />
          </div>
        </div>
      </div>

      {showSelectionMenu && (
        <TextSelectionMenu
          x={selectionMenuPos.x}
          y={selectionMenuPos.y}
          selectedText={selectedText}
          onClose={() => setShowSelectionMenu(false)}
          onHighlight={handleHighlight}
          onNote={handleNote}
          onAskAI={() => setShowSelectionMenu(false)}
        />
      )}

      {showNoteModal && (
        <NoteModal
          selectedText={selectedText}
          onSave={handleSaveNote}
          onClose={() => setShowNoteModal(false)}
        />
      )}

      {/* 하단 네비게이션 */}
      <footer className="bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 px-4 py-3">
        <div className="flex items-center justify-between max-w-4xl mx-auto">
          <button
            onClick={goToPrevPage}
            disabled={currentPage <= 1}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
            title="이전 페이지"
          >
            <FiChevronLeft size={24} />
          </button>

          <div className="flex-1 mx-4">
            <div className="flex items-center justify-center gap-4 mb-2">
              <span className="text-sm text-gray-600 dark:text-gray-400">
                {currentPage} / {totalPages}
              </span>
              <span className="text-sm text-gray-500 dark:text-gray-500">
                ({progress}%)
              </span>
            </div>
            <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
              <div
                className="h-full gradient-primary transition-all duration-300"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>

          <button
            onClick={goToNextPage}
            disabled={currentPage >= totalPages}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
            title="다음 페이지"
          >
            <FiChevronRight size={24} />
          </button>
        </div>

        <div className="flex items-center justify-center gap-2 mt-2 pt-2 border-t border-gray-100 dark:border-gray-700">
          <button
            onClick={zoomOut}
            disabled={scale <= 0.5}
            className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-30 transition-colors"
            title="축소"
          >
            <FiZoomOut size={16} />
          </button>
          <span className="text-xs text-gray-500 dark:text-gray-400 min-w-[45px] text-center">
            {Math.round(scale * 100)}%
          </span>
          <button
            onClick={zoomIn}
            disabled={scale >= 3.0}
            className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-30 transition-colors"
            title="확대"
          >
            <FiZoomIn size={16} />
          </button>
          <button
            onClick={fitToWidth}
            className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            title="화면에 맞추기"
          >
            <FiMaximize2 size={16} />
          </button>
        </div>
      </footer>
    </div>
  );
};

export default PDFViewer;
